import { InfiniteData, infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import api from "~/shared/api/core/api";
import {
  ParticipantSearchResponse,
  SavedRoleDetailResponse,
  SavedRolesResponse,
} from "~/shared/api/participants/participants.types";
import { Params } from "~/shared/api/types/params";
import { RoleType } from "~/shared/constants/savedRoles.constants";
import { client } from "~/shared/lib/client";
import { addFullNameToDataItems } from "~/shared/util/addFullNameToDataItems";
import { createInfiniteDataTransform } from "~/shared/util/createInfiniteDataTransform";
import { UserProfileResponse } from "../users/users.types";

export const PARTICIPANTS_KEYS = {
  all: ["participant"] as const,
  list: (params: Params) => [...PARTICIPANTS_KEYS.all, "list", params] as const,
  detail: (id?: string) => [...PARTICIPANTS_KEYS.all, id] as const,
  highTouch: (params: Params) => [...PARTICIPANTS_KEYS.all, "high-touch", params] as const,
  userStatuses: (params: Params) => [...PARTICIPANTS_KEYS.all, "user-statuses", params] as const,
  savedRoles: (participantId: string) => [...PARTICIPANTS_KEYS.detail(participantId), "savedRoles"] as const,
  savedRolesList: (participantId: string, params: Params) =>
    [...PARTICIPANTS_KEYS.savedRoles(participantId), params] as const,
  savedRolesDetail: (participantId: string, savedRoleId: number) =>
    [...PARTICIPANTS_KEYS.savedRoles(participantId), "detail", savedRoleId] as const,
  roleGroupsList: (participantId: string, params: Params) =>
    [...PARTICIPANTS_KEYS.detail(participantId), "role-groups", params] as const,
  members: {
    all: () => [...PARTICIPANTS_KEYS.all, "members"] as const,
    listGameplans: (participantId: string, params?: Params) =>
      [...PARTICIPANTS_KEYS.members.all(), "listGameplans", participantId, params] as const,
  },
};

export const PARTICIPANTS_QUERIES = {
  participant: (id?: string) => {
    return queryOptions({
      queryKey: PARTICIPANTS_KEYS.detail(id),
      queryFn: async ({ signal }) => {
        if (!id) return null;

        const response = await api.get<UserProfileResponse>(`/participants/${id}`, { signal });

        return response.data;
      },
    });
  },
  search: (params: Params) => {
    return queryOptions({
      queryKey: [...PARTICIPANTS_KEYS.all, "search", params] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<ParticipantSearchResponse>("/search/participants", {
          ...params,
          signal,
        });

        return response.data;
      },
      staleTime: 1000 * 60 * 1, // 1 minute
    });
  },
  infiniteSearch: (params: Record<string, unknown>) => {
    const { limit = 50 } = params ?? {};

    return infiniteQueryOptions({
      queryKey: [
        ...PARTICIPANTS_KEYS.all,
        "search",
        {
          params: {
            ...params,
            limit,
          },
        },
      ] as const,
      queryFn: async ({ pageParam = 0, signal }) => {
        const response = await api.get<ParticipantSearchResponse>("/search/participants", {
          params: {
            ...params,
            limit,
            page: pageParam,
          },
          signal,
        });

        return response.data;
      },
      staleTime: 1000 * 60 * 1, // 1 minute
      initialPageParam: 0,
      getNextPageParam: (lastPage, _, lastPageParam) => {
        if (lastPage.items.length < (limit as number)) {
          return undefined;
        }

        return lastPageParam + 1;
      },
    });
  },
  searchFacets: (params: Params) => {
    return queryOptions({
      queryKey: [...PARTICIPANTS_KEYS.all, "search-facets", params] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<{
          data: {
            facets: Array<{
              highlighted: string;
              count: number;
              value: string;
            }>;
          };
        }>("/search/participants/facets", {
          ...params,
          signal,
        });
        return response.data.data;
      },
      staleTime: 1000 * 60 * 1, // 1 minute
      select: (data) => {
        return data.facets.map((item) => ({
          ...item,
          id: item.value,
          name: item.value,
        }));
      },
    });
  },
  infiniteUserStatusesList: (params: { [key: string]: unknown }) => {
    const { limit = 20 } = params ?? {};

    return infiniteQueryOptions({
      queryFn: async ({ pageParam = 1, signal }) => {
        const response = await api.get(`/user-statuses`, {
          params: {
            ...params,
            limit,
            skip: (pageParam - 1) * (limit as number),
          },
          signal,
        });

        return response.data;
      },
      queryKey: PARTICIPANTS_KEYS.userStatuses({
        params: {
          ...params,
          limit,
        },
      }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, _, lastPageParam) => {
        if (lastPage.items.length < (limit as number)) {
          return undefined;
        }

        return lastPageParam + 1;
      },
      select: (
        data: InfiniteData<
          {
            items: {
              id: number;
              type: string;
            }[];
          },
          number
        >,
      ) => {
        return createInfiniteDataTransform(data, (item) => {
          return {
            ...item,
            name: item.type,
          };
        });
      },
    });
  },
  infiniteParticipantsList: (params: { [key: string]: unknown }) => {
    const { limit = 20, all = "true", orderDir = "ASC", orderBy = "name" } = params ?? {};
    return infiniteQueryOptions({
      queryFn: async ({ pageParam = 1, signal }) => {
        const response = await api.get(`/participants`, {
          params: {
            ...params,
            limit,
            all,
            orderBy,
            orderDir,
            skip: (pageParam - 1) * (limit as number),
          },
          signal,
        });

        return response.data;
      },
      queryKey: PARTICIPANTS_KEYS.list({
        params: {
          ...params,
          limit,
          all,
          orderBy,
          orderDir,
        },
      }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, _, lastPageParam) => {
        if (lastPage.items.length < (limit as number)) {
          return undefined;
        }

        return lastPageParam + 1;
      },
      select: addFullNameToDataItems,
    });
  },
  savedRolesList: (participantId: string, params: Params) => {
    return queryOptions({
      queryKey: PARTICIPANTS_KEYS.savedRolesList(participantId, params),
      queryFn: async ({ signal }) => {
        const response = await api.get<SavedRolesResponse>(`/participants/${participantId}/saved/roles`, {
          ...params,
          signal,
        });

        return response.data;
      },
    });
  },
  savedRoleDetail: <TRoleType extends RoleType>(participantId: string, savedRoleId: number) => {
    return queryOptions({
      queryKey: PARTICIPANTS_KEYS.savedRolesDetail(participantId, savedRoleId),
      queryFn: async ({ signal }) => {
        const response = await api.get<SavedRoleDetailResponse<TRoleType>>(
          `/participants/${participantId}/saved/roles/${savedRoleId}`,
          {
            signal,
          },
        );

        return response.data;
      },
    });
  },
  infiniteRoleGroupsList: (participantId: string, params: Record<string, unknown>) => {
    const { limit = 50 } = params ?? {};

    return infiniteQueryOptions({
      queryKey: PARTICIPANTS_KEYS.roleGroupsList(participantId, {
        params: {
          ...params,
          limit,
        },
      }),
      queryFn: async ({ pageParam = 1, signal }) => {
        const { data, error } = await client.GET("/participants/{participantId}/role-groups", {
          params: {
            path: {
              participantId: parseInt(participantId),
            },
            query: {
              ...params,
              limit: limit as number,
              skip: (pageParam - 1) * (limit as number),
            },
          },
          signal,
        });

        if (error) {
          throw new Error("Error fetching role groups");
        }

        return data;
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage, _, lastPageParam) => {
        if (lastPage.items.length < (limit as number)) {
          return undefined;
        }

        return lastPageParam + 1;
      },
    });
  },
  members: {
    listGameplans: (participantId: string, params: Params = {}) => {
      return queryOptions({
        queryKey: PARTICIPANTS_KEYS.members.listGameplans(participantId, params),
        queryFn: async ({ signal }) => {
          const response = await api.get<ParticipantSearchResponse>(`/participants/${participantId}/brainstorms`, {
            ...params,
            signal,
          });

          return response.data;
        },
      });
    },
  },
};
